import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles } from 'lucide-react';
import { arenaAudio } from '../../services/ArenaAudioService';

export interface HeartsBrokenBannerProps {
  heartsBroken?: boolean;
  isPlayPhase?: boolean;
  lastCardPlayed?: { suit?: string; rank?: string; id?: string } | null;
  event?: import('../../presentation/GamePresentationTypes').GameEventItem;
  gameState?: import('../../state/ArenaTypes').ArenaGameState;
}



/**
 * Full-Screen Broken Glass Spiderweb Overlay
 * Renders jagged opaque crystalline fracture lines radiating across the viewport.
 */
const FullScreenGlassFracture: React.FC = () => {
  return (
    <div className="absolute inset-0 pointer-events-none select-none overflow-hidden z-10">
      <svg
        viewBox="0 0 1000 1000"
        preserveAspectRatio="none"
        className="w-full h-full opacity-90 filter drop-shadow-[0_0_8px_rgba(255,255,255,0.8)]"
      >
        <defs>
          <linearGradient id="glassFractureWhite" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="0.95" />
            <stop offset="50%" stopColor="#e2e8f0" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#94a3b8" stopOpacity="0.6" />
          </linearGradient>
        </defs>

        {/* Primary Radial Impact Cracks radiating from upper-center impact point (x=500, y=280) */}
        <motion.path
          d="M 500,280 L 410,210 L 320,160 L 190,90 L 40,30"
          stroke="url(#glassFractureWhite)"
          strokeWidth="2.5"
          fill="none"
          strokeLinecap="round"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: [0, 1, 0.85] }}
          transition={{ duration: 0.35, ease: 'easeOut' }}
        />
        <motion.path
          d="M 500,280 L 590,200 L 710,140 L 860,80 L 980,20"
          stroke="url(#glassFractureWhite)"
          strokeWidth="2.5"
          fill="none"
          strokeLinecap="round"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: [0, 1, 0.85] }}
          transition={{ duration: 0.35, ease: 'easeOut', delay: 0.02 }}
        />
        <motion.path
          d="M 500,280 L 390,320 L 260,380 L 120,440 L 10,510"
          stroke="url(#glassFractureWhite)"
          strokeWidth="2.5"
          fill="none"
          strokeLinecap="round"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: [0, 1, 0.85] }}
          transition={{ duration: 0.38, ease: 'easeOut', delay: 0.04 }}
        />
        <motion.path
          d="M 500,280 L 610,330 L 740,400 L 880,480 L 990,560"
          stroke="url(#glassFractureWhite)"
          strokeWidth="2.5"
          fill="none"
          strokeLinecap="round"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: [0, 1, 0.85] }}
          transition={{ duration: 0.38, ease: 'easeOut', delay: 0.05 }}
        />
        <motion.path
          d="M 500,280 L 470,390 L 430,510 L 380,680 L 320,890 L 290,1000"
          stroke="url(#glassFractureWhite)"
          strokeWidth="2"
          fill="none"
          strokeLinecap="round"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: [0, 1, 0.8] }}
          transition={{ duration: 0.42, ease: 'easeOut', delay: 0.07 }}
        />
        <motion.path
          d="M 500,280 L 540,390 L 580,520 L 640,700 L 710,910 L 740,1000"
          stroke="url(#glassFractureWhite)"
          strokeWidth="2"
          fill="none"
          strokeLinecap="round"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: [0, 1, 0.8] }}
          transition={{ duration: 0.42, ease: 'easeOut', delay: 0.08 }}
        />

        {/* Concentric Spiderweb Glass Cross-Fractures */}
        <motion.path
          d="M 410,210 L 460,200 L 540,195 L 590,200 L 610,260 L 610,330 L 540,390 L 470,390 L 390,320 L 410,210"
          stroke="url(#glassFractureWhite)"
          strokeWidth="1.8"
          fill="none"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: [0, 1, 0.75] }}
          transition={{ duration: 0.45, ease: 'easeOut', delay: 0.08 }}
        />
        <motion.path
          d="M 320,160 L 430,120 L 570,110 L 710,140 L 740,260 L 740,400 L 580,520 L 430,510 L 260,380 L 320,160"
          stroke="url(#glassFractureWhite)"
          strokeWidth="1.5"
          fill="none"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: [0, 1, 0.7] }}
          transition={{ duration: 0.5, ease: 'easeOut', delay: 0.12 }}
        />

        {/* Diagonal Secondary Stress Cracks */}
        <motion.path
          d="M 460,200 L 430,120 M 540,195 L 570,110 M 610,260 L 740,260 M 540,390 L 580,520 M 470,390 L 430,510 M 390,320 L 260,380"
          stroke="#ffffff"
          strokeWidth="1.2"
          fill="none"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: [0, 1, 0.65] }}
          transition={{ duration: 0.4, ease: 'easeOut', delay: 0.15 }}
        />
      </svg>
    </div>
  );
};

/**
 * Animated Hearts Broken Graphic Display
 * Displays hearts_broken.webp instead of the vector SVG cracking symbol.
 */
const AnimatedHeartsBrokenImage: React.FC = () => {
  return (
    <div className="relative w-40 h-40 sm:w-52 sm:h-52 flex items-center justify-center pointer-events-none select-none">
      <img
        src="/Images/hearts_broken.webp"
        alt="Hearts Broken"
        className="w-full h-full object-contain filter drop-shadow-[0_0_24px_rgba(255,255,255,0.7)]"
      />
    </div>
  );
};

export const HeartsBrokenBanner: React.FC<HeartsBrokenBannerProps> = ({
  heartsBroken = false,
  isPlayPhase = false,
  lastCardPlayed = null,
  event,
  gameState,
}) => {
  const [showBanner, setShowBanner] = React.useState(Boolean(event));
  const isQueenFromEvent = Boolean(event?.payload?.isQueen);
  const [isQueenTrigger, setIsQueenTrigger] = React.useState(isQueenFromEvent);
  const prevHeartsBrokenRef = React.useRef(heartsBroken);
  const executedEventIdsRef = React.useRef<Set<string>>(new Set());
  const triggeredHandsRef = React.useRef<Set<number>>(new Set());

  // When rendered via generic event architecture
  React.useEffect(() => {
    if (event) {
      const isQueen = Boolean(event.payload?.isQueen);
      setIsQueenTrigger(isQueen);
      setShowBanner(true);

      if (!executedEventIdsRef.current.has(event.id)) {
        executedEventIdsRef.current.add(event.id);
        try {
          arenaAudio.playHeartsBroken();
        } catch {}
      }

      const timer = setTimeout(() => {
        setShowBanner(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [event]);

  // Backward compatibility when rendered via prop diffing (strictly once per hand)
  React.useEffect(() => {
    if (event) return;
    const currentHand = gameState?.handNumber ?? 0;
    const alreadyTriggeredForHand = triggeredHandsRef.current.has(currentHand);

    if (!alreadyTriggeredForHand && !prevHeartsBrokenRef.current && heartsBroken && isPlayPhase) {
      triggeredHandsRef.current.add(currentHand);
      const isQueen =
        lastCardPlayed?.id === 'S_Q' ||
        lastCardPlayed?.id === 'QS' ||
        (lastCardPlayed?.suit === 'S' && lastCardPlayed?.rank === 'Q');

      setIsQueenTrigger(Boolean(isQueen));
      setShowBanner(true);

      try {
        arenaAudio.playHeartsBroken();
      } catch {}

      const timer = setTimeout(() => {
        setShowBanner(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
    prevHeartsBrokenRef.current = heartsBroken;
  }, [heartsBroken, isPlayPhase, lastCardPlayed, event, gameState?.handNumber]);

  return (
    <AnimatePresence>
      {showBanner && (
        <div
          data-testid="hearts-broken-modal"
          className="fixed inset-0 z-[120] pointer-events-none select-none flex flex-col items-center justify-start pt-14 sm:pt-20 md:pt-24 p-4 overflow-hidden"
        >
          {/* Full-screen Dark Translucent Glass Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: 'easeOut' }}
            className="absolute inset-0 bg-black/50 backdrop-blur-[2px]"
          />

          {/* Full-Screen Broken Glass Spiderweb Cracks Overlay */}
          <FullScreenGlassFracture />

          {/* Modal Content Positioned Just North of the Felt Trick so Center Trick Remains 100% Visible */}
          <motion.div
            key="hearts-broken-card"
            initial={{ opacity: 0, scale: 0.75, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.85, y: -16 }}
            transition={{ type: 'spring', stiffness: 360, damping: 24 }}
            className="relative z-30 flex flex-col items-center justify-center gap-3 px-6 sm:px-10 py-3 sm:py-4 bg-transparent border-0 shadow-none text-center max-w-sm sm:max-w-md pointer-events-none select-none"
          >
            {/* Animated Hearts Broken APNG graphic */}
            <AnimatedHeartsBrokenImage />

            {/* Banner Text / Subtitle */}
            <div className="flex flex-col items-center gap-1 mt-0.5">
              <span className="text-base sm:text-lg font-black tracking-widest uppercase font-mono flex items-center justify-center gap-2 drop-shadow-[0_2px_10px_rgba(0,0,0,1)] text-white">
                {isQueenTrigger ? '♠ QUEEN OF SPADES — HEARTS BROKEN!' : '💔 HEARTS BROKEN!'}
                <Sparkles className="w-5 h-5 text-amber-300 animate-spin inline-block ml-1" style={{ animationDuration: '3s' }} />
              </span>
              <span className="text-xs sm:text-sm font-semibold tracking-wider text-slate-200 drop-shadow-[0_1px_4px_rgba(0,0,0,0.9)]">
                Hearts may now be led freely.
              </span>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

